/**
 * First-visit field manual: keyboard/mouse control chart plus the project
 * credits, shown once (localStorage) and reopenable from the menus.
 *
 * The chart is one generated SVG — keys laid out from a row table, callout
 * labels anchored to real key centres — so the diagram can never drift from
 * the actual bindings in core/Input.js without someone editing this table.
 */

const SEEN_KEY = 'blackout.intro.seen';

const U = 60;           // key pitch (px in viewBox units)
const KEY_H = 54;
const KX = 345, KY = 130; // keyboard origin

/* [legend, units, id] — ids only where a callout or highlight needs the key */
const ROWS = [
  [['esc', 1.5, 'Esc'], ['', 0.35], ['F1', 1.0625], ['F2', 1.0625], ['F3', 1.0625], ['F4', 1.0625], ['F5', 1.0625], ['F6', 1.0625], ['F7', 1.0625], ['F8', 1.0625], ['F9', 1.0625], ['F10', 1.0625], ['F11', 1.0625], ['F12', 1.0625]],
  [['~', 1], ['1', 1, '1'], ['2', 1, '2'], ['3', 1, '3'], ['4', 1, '4'], ['5', 1], ['6', 1], ['7', 1], ['8', 1], ['9', 1], ['0', 1], ['-', 1], ['=', 1], ['delete', 1.6]],
  [['tab', 1.6], ['Q', 1, 'Q'], ['W', 1, 'W'], ['E', 1, 'E'], ['R', 1, 'R'], ['T', 1], ['Y', 1], ['U', 1], ['I', 1], ['O', 1], ['P', 1], ['[', 1], [']', 1], ['\\', 1]],
  [['caps lock', 1.9], ['A', 1, 'A'], ['S', 1, 'S'], ['D', 1, 'D'], ['F', 1, 'F'], ['G', 1], ['H', 1], ['J', 1], ['K', 1], ['L', 1], [';', 1], ['\'', 1], ['return', 1.7]],
  [['shift', 2.45, 'LShift'], ['Z', 1], ['X', 1], ['C', 1, 'C'], ['V', 1, 'V'], ['B', 1], ['N', 1], ['M', 1], [',', 1], ['.', 1], ['/', 1], ['shift', 2.15]],
  [['ctrl', 1.3, 'LCtrl'], ['alt', 1.3], ['cmd', 1.6], ['', 6.0, 'Space'], ['cmd', 1.6], ['alt', 1.3], ['ctrl', 1.5]],
];

const HIGHLIGHT = new Set(['Esc', 'W', 'A', 'S', 'D', 'LShift', 'LCtrl', 'C', 'Q', 'E', 'R', '1', '2', '3', '4', 'F', 'V', 'Space']);

/* {text, keys[], anchor:[x,y], align, target:keyId} */
const CALLOUTS = [
  { text: 'オプション', keys: ['Esc'], anchor: [330, 165], align: 'end', target: 'Esc' },
  { text: '移動', keys: ['W', 'A', 'S', 'D'], anchor: [330, 245], align: 'end', target: 'A' },
  { text: 'スプリント', keys: ['LShift'], anchor: [330, 325], align: 'end', target: 'LShift' },
  { text: 'しゃがみ', keys: ['LCtrl', 'C'], anchor: [330, 405], align: 'end', target: 'LCtrl' },
  { text: 'リーン', keys: ['Q', 'E'], anchor: [500, 62], align: 'middle', target: 'Q' },
  { text: 'リロード', keys: ['R'], anchor: [800, 62], align: 'middle', target: 'R' },
  { text: '武器スロット', keys: ['1', '2', '3', '4'], anchor: [1100, 62], align: 'middle', target: '3' },
  { text: '武器切替', keys: ['F'], anchor: [1245, 245], align: 'start', target: 'F' },
  { text: '近接攻撃', keys: ['V'], anchor: [1245, 350], align: 'start', target: 'V' },
  { text: 'ジャンプ', keys: ['Space'], anchor: [790, 562], align: 'middle', target: 'Space', dotAbove: true },
];

const MOUSE = { cx: 790, top: 640, w: 116, h: 180 };

const MOUSE_CALLOUTS = [
  { text: '射撃', keys: ['左クリック'], anchor: [560, 690], align: 'end', to: [MOUSE.cx - 30, MOUSE.top + 40] },
  { text: 'エイム / 構える', keys: ['右クリック'], anchor: [1020, 690], align: 'start', to: [MOUSE.cx + 30, MOUSE.top + 40] },
  { text: '近接攻撃', keys: ['中クリック'], anchor: [560, 775], align: 'end', to: [MOUSE.cx - 8, MOUSE.top + 52] },
  { text: '武器切替', keys: ['ホイール'], anchor: [1020, 775], align: 'start', to: [MOUSE.cx + 8, MOUSE.top + 52] },
];

const SVG_NS = 'http://www.w3.org/2000/svg';

function svgEl(tag, attrs, parent) {
  const n = document.createElementNS(SVG_NS, tag);
  for (const [k, v] of Object.entries(attrs)) n.setAttribute(k, v);
  if (parent) parent.appendChild(n);
  return n;
}

/* Rough advance widths; close enough for badge sizing at this scale. */
function textW(s, size) {
  let w = 0;
  for (const ch of s) w += ch.charCodeAt(0) > 0x2e80 ? size : size * 0.62;
  return w;
}

export class IntroOverlay {
  constructor(hud) {
    this.hud = hud;
    this.ctx = null;
    this.visible = false;
    this.el = null;
  }

  init(ctx, root) {
    this.ctx = ctx;
    this.el = document.createElement('div');
    this.el.className = 'intro-layer';
    this.el.appendChild(this._buildShell());
    root.appendChild(this.el);
    if (!ctx.config?.headless && !localStorage.getItem(SEEN_KEY)) this.show();
  }

  show() {
    this.visible = true;
    this.el.classList.add('show');
    if (this.ctx?.input) this.ctx.input.enabled = false;
    if (document.pointerLockElement) document.exitPointerLock?.();
  }

  hide(lock = false) {
    this.visible = false;
    this.el.classList.remove('show');
    try { localStorage.setItem(SEEN_KEY, '1'); } catch { /* private mode */ }
    // The menus own input while they are up; only hand it back if none is open.
    if (this.ctx?.input && !this.hud?.menus?.isOpen) this.ctx.input.enabled = true;
    if (lock) {
      // Headless and some embedded contexts refuse pointer lock; entering the
      // game without it is fine — the first canvas click locks as usual.
      try { Promise.resolve(this.ctx?.engine?.canvas?.requestPointerLock?.()).catch(() => {}); } catch { /* ignore */ }
    }
  }

  dispose() {
    this.el?.remove();
  }

  /* ------------------------------------------------------------------ DOM */

  _buildShell() {
    const shell = document.createElement('div');
    shell.className = 'intro-shell';

    const head = document.createElement('div');
    head.className = 'intro-head';
    head.innerHTML = `
      <div class="intro-eyebrow">OPERATION BLACKOUT&ensp;//&ensp;FIELD MANUAL</div>
      <h1 class="intro-title">操作方法 <span>KEYBOARD &amp; MOUSE</span></h1>`;
    shell.appendChild(head);

    shell.appendChild(this._buildChart());

    const start = document.createElement('button');
    start.className = 'intro-start';
    start.innerHTML = 'ゲーム開始 <span>CLICK TO DEPLOY</span>';
    start.addEventListener('click', () => this.hide(true));
    shell.appendChild(start);

    const note = document.createElement('div');
    note.className = 'intro-note';
    note.textContent = '※ 動作が重い場合は画質が自動で調整されます。Esc で設定を開けます。このガイドはメニューの INFO からいつでも再表示できます。';
    shell.appendChild(note);

    const about = document.createElement('div');
    about.className = 'intro-about';
    about.innerHTML = `
      <div class="intro-about-head">ABOUT — この作品について</div>
      <p>OPERATION BLACKOUT は、AI コーディング実験プロジェクトとして制作された Three.js 製ブラウザ FPS デモです。
      コード・グラフィック・品質検証のすべてを、AI エージェントが「Gauntlet Loop」と呼ばれる反復批評ループで自律的に作り込みました。未完成の実験作であり、内容は予告なく変わります。</p>
      <p>制作・運営: たきコーチ
      <a href="https://x.com/takicoach_ai" target="_blank" rel="noopener noreferrer">@takicoach_ai</a></p>
      <p>Special thanks: Gauntlet Loop プロンプト考案者
      <a href="https://x.com/mattshumer_" target="_blank" rel="noopener noreferrer">@mattshumer_</a>
      — 本実験は氏の公開プロンプトに着想を得ています。</p>`;
    shell.appendChild(about);

    return shell;
  }

  /* ------------------------------------------------------------ the chart */

  _buildChart() {
    const svg = svgEl('svg', { viewBox: '0 0 1580 830', class: 'intro-chart', role: 'img' });
    const lines = svgEl('g', { class: 'ic-lines' }, svg);
    const keysG = svgEl('g', {}, svg);
    const labels = svgEl('g', {}, svg);

    /* keys */
    const centers = new Map();
    ROWS.forEach((row, r) => {
      let x = KX;
      for (const [legend, units, id] of row) {
        const w = units * U - 6;
        if (legend === '' && !id) { x += units * U; continue; } // spacer
        const y = KY + r * U;
        if (id) centers.set(id, [x + w / 2, y + KEY_H / 2]);
        const hot = id && HIGHLIGHT.has(id);
        svgEl('rect', {
          x, y, width: w, height: KEY_H, rx: 9,
          class: 'ic-key' + (hot ? ' hot' : ''),
        }, keysG);
        if (legend) {
          const small = legend.length > 1;
          svgEl('text', {
            x: small ? x + 10 : x + w / 2,
            y: small ? y + KEY_H - 12 : y + KEY_H / 2 + 6,
            'text-anchor': small ? 'start' : 'middle',
            class: 'ic-key-t' + (hot ? ' hot' : ''),
          }, keysG).textContent = legend;
        }
        x += units * U;
      }
    });

    /* keyboard callouts */
    for (const c of CALLOUTS) {
      const target = centers.get(c.target);
      const dot = this._layoutLabel(labels, c);
      if (target) svgEl('line', { x1: dot[0], y1: dot[1], x2: target[0], y2: target[1], class: 'ic-line' }, lines);
      svgEl('circle', { cx: dot[0], cy: dot[1], r: 4.5, class: 'ic-dot' }, lines);
    }

    /* mouse */
    const m = MOUSE;
    svgEl('rect', { x: m.cx - m.w / 2, y: m.top, width: m.w, height: m.h, rx: m.w / 2, class: 'ic-mouse' }, keysG);
    svgEl('line', { x1: m.cx, y1: m.top + 4, x2: m.cx, y2: m.top + 62, class: 'ic-mouse-split' }, keysG);
    svgEl('rect', { x: m.cx - 7, y: m.top + 34, width: 14, height: 34, rx: 7, class: 'ic-wheel' }, keysG);
    for (const c of MOUSE_CALLOUTS) {
      const dot = this._layoutLabel(labels, c);
      svgEl('line', { x1: dot[0], y1: dot[1], x2: c.to[0], y2: c.to[1], class: 'ic-line' }, lines);
      svgEl('circle', { cx: dot[0], cy: dot[1], r: 4.5, class: 'ic-dot' }, lines);
    }

    return svg;
  }

  /**
   * Lay a "text [KEY][KEY]" label out around its anchor.
   * Returns the point the leader line should start from.
   */
  _layoutLabel(g, c) {
    const [ax, ay] = c.anchor;
    const size = 20;
    const badgeH = 26, badgeGap = 7, pad = 9;
    const tw = textW(c.text, size);
    const badges = c.keys.map((k) => [k, Math.max(badgeH, pad * 2 + textW(k, 14))]);
    const total = tw + badges.reduce((s, [, w]) => s + w + badgeGap, 0);

    let x0;
    if (c.align === 'end') x0 = ax - total;
    else if (c.align === 'middle') x0 = ax - total / 2;
    else x0 = ax;

    svgEl('text', { x: x0, y: ay + 7, class: 'ic-label' }, g).textContent = c.text;
    let bx = x0 + tw + badgeGap;
    for (const [k, w] of badges) {
      svgEl('rect', { x: bx, y: ay - badgeH / 2, width: w, height: badgeH, rx: 4, class: 'ic-badge' }, g);
      svgEl('text', { x: bx + w / 2, y: ay + 5, 'text-anchor': 'middle', class: 'ic-badge-t' }, g).textContent = k;
      bx += w + badgeGap;
    }

    /* leader line starts at the side of the label facing the diagram */
    if (c.align === 'end') return [ax + 12, ay];
    if (c.align === 'start') return [x0 - 12, ay];
    return [ax, c.dotAbove ? ay - 24 : ay + 22];
  }
}
